/**
 * Money crosses the API in minor units. The backend spec states it explicitly
 * for dish and topping prices ("Price represented in minor unit"), and the same
 * convention holds for ingredient prices, employment rates and every amount
 * coming from the insights API.
 *
 * The UI always speaks major units: anything read from the API goes through
 * `formatPrice`/`minorToMajor`/`formatPriceForInput`, and anything typed by the
 * user goes through `majorToMinor` before it is sent back. Nothing should
 * divide or multiply by 100 on its own, and 100 is not always the answer.
 *
 * The venue's currency decides both the symbol and how many minor units make a
 * major one: 100 for PLN and EUR, 1 for JPY, 1000 for KWD. The backend reads it
 * off the restaurant rather than a platform default, so this does too, see
 * `restaurantCurrency` below.
 */

const DEFAULT_CURRENCY = 'PLN'

const DEFAULT_LOCALE = 'pl-PL'

/** Digits are looked up once per currency; a table is formatted cell by cell. */
const digitsByCurrency = new Map<string, number>()

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/**
 * ISO 4217 code of the restaurant being managed, stored beside its id at login.
 *
 * Read from localStorage rather than passed down: every price in the app would
 * otherwise need the currency threaded through it, and it is a property of the
 * session, not of any one table. Falls back to PLN, which is what a session
 * that predates this key was already being shown.
 */
export const restaurantCurrency = (): string =>
  localStorage.getItem('currency') || DEFAULT_CURRENCY

/**
 * Fraction digits the currency uses, the minor-unit exponent.
 *
 * Intl carries the table, so nothing here has to. An unrecognised code makes
 * the constructor throw, which would take down every amount on the page, so it
 * falls back to the two digits most currencies use.
 */
const currencyDigits = (currency: string): number => {
  const cached = digitsByCurrency.get(currency)
  if (cached !== undefined) return cached

  let digits = 2
  try {
    // Typed optional by the DOM lib even though a currency format always
    // resolves it; the fallback keeps the two-digit default either way.
    digits =
      new Intl.NumberFormat(DEFAULT_LOCALE, {
        style: 'currency',
        currency
      }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    digits = 2
  }

  digitsByCurrency.set(currency, digits)
  return digits
}

const minorUnitsPerMajor = (currency: string): number =>
  10 ** currencyDigits(currency)

/** Raw numeric conversion, use for chart series, where a number is needed. */
export const minorToMajor = (minorUnits: number): number =>
  minorUnits / minorUnitsPerMajor(restaurantCurrency())

/**
 * Parses whatever the user typed ("12,50", "12.50", "12,50 zł", 12.5) into
 * whole minor units. Returns undefined for empty/unparsable input, so callers
 * can decide between omitting the field and falling back to 0.
 */
export const majorToMinor = (
  value: string | number | null | undefined
): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined

  const parsed = isNumber(value)
    ? value
    : Number(String(value).replace(',', '.').replace(/[^0-9.-]/g, ''))

  if (!Number.isFinite(parsed)) return undefined

  return Math.round(parsed * minorUnitsPerMajor(restaurantCurrency()))
}

/**
 * The currency's symbol on its own, for a field's unit adornment or a figure
 * whose number is rendered separately, where `formatPrice` would put the two
 * together. Falls back to the ISO code, which is what Intl itself shows for a
 * currency the locale has no symbol for (USD in pl-PL).
 */
export const currencySymbol = (locale: string = DEFAULT_LOCALE): string => {
  const currency = restaurantCurrency()

  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency })
        .formatToParts(0)
        .find(part => part.type === 'currency')?.value ?? currency
    )
  } catch {
    return currency
  }
}

interface FormatPriceOptions {
  /** Appends the currency. Turn off where the layout renders the unit itself. */
  withCurrency?: boolean
  locale?: string
}

/** 2800 -> "28,00 zł" in a PLN venue, "28,00 €" in a EUR one. */
export const formatPrice = (
  minorUnits: number | null | undefined,
  options: FormatPriceOptions = {}
): string => {
  const { withCurrency = true, locale = DEFAULT_LOCALE } = options

  if (!isNumber(minorUnits)) return ''

  const currency = restaurantCurrency()
  const digits = currencyDigits(currency)
  const amount = minorUnits / 10 ** digits

  const plain = amount.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })

  if (!withCurrency) return plain

  try {
    return amount.toLocaleString(locale, { style: 'currency', currency })
  } catch {
    // Same fallback as currencyDigits: an amount without its symbol beats no
    // amount at all.
    return plain
  }
}

/**
 * 2800 -> "28.00". Value for a price input: always a dot separator, because
 * the form validators strip everything outside [0-9.-] and a comma would
 * silently turn 28,00 into 2800. Digits follow the currency, so a JPY venue
 * gets "2800" rather than a fractional yen.
 */
export const formatPriceForInput = (
  minorUnits: number | null | undefined
): string => {
  if (!isNumber(minorUnits)) return ''

  const digits = currencyDigits(restaurantCurrency())
  return (minorUnits / 10 ** digits).toFixed(digits)
}
